import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated collapse wrapper — smoothly reveals/hides children.
 *
 * Uses CSS `grid-template-rows` (0fr ↔ 1fr) for natural height animation.
 * Children stay mounted during the closing animation and unmount only
 * after the transition completes.
 *
 * Duration matches ToggleSwitch's 200ms for a synchronized feel:
 *   - Toggle thumb slides while content collapses in parallel
 *   - Opacity fades slightly faster (70% of duration) for a "sink" effect
 *
 * Opening is also animated (0fr → 1fr) so the experience is smooth both ways.
 *
 * @param {Object}  props
 * @param {boolean} props.open       — Whether children are visible
 * @param {number}  [props.duration] — Transition ms (default: 200, matches ToggleSwitch)
 * @param {React.ReactNode} props.children
 */
export function CollapseTransition({ open, children, duration = 200 }) {
  // `mounted` controls whether children exist in the DOM.
  // `animOpen` controls the CSS target state (0fr vs 1fr).
  const [mounted, setMounted] = useState(open);
  const [animOpen, setAnimOpen] = useState(open);
  const closeTimer = useRef(null);

  useEffect(() => {
    clearTimeout(closeTimer.current);

    if (open) {
      // ── Opening ──
      // Mount children first (at 0fr), then animate to 1fr next frame.
      setMounted(true);
      // Double rAF: first ensures the mount render is painted at 0fr,
      // second triggers the CSS transition to 1fr.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimOpen(true);
        });
      });
    } else {
      // ── Closing ──
      // Animate to 0fr immediately, unmount after transition finishes.
      setAnimOpen(false);
      closeTimer.current = setTimeout(() => {
        setMounted(false);
      }, duration + 30); // small buffer so the last frame paints at 0fr
    }

    return () => clearTimeout(closeTimer.current);
  }, [open, duration]);

  if (!mounted) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: animOpen ? '1fr' : '0fr',
        opacity: animOpen ? 1 : 0,
        transition: [
          `grid-template-rows ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          `opacity ${Math.round(duration * 0.7)}ms ease`,
        ].join(', '),
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Hook that delays a boolean transition from true → false.
 *
 * Designed to keep a grid item at `block-expanded` while CollapseTransition
 * animates the inner content closed, then snap to `block-tile` after.
 *
 * - true  → true:  immediate (expand instantly)
 * - false → true:  immediate
 * - true  → false: delayed by `delay` ms
 * - false → false: stays false
 *
 * @param {boolean} value — the live boolean (e.g. `isEnabled`)
 * @param {number}  [delay=230] — ms to wait before returning false (should
 *                                 match or slightly exceed CollapseTransition duration)
 * @returns {boolean}
 */
export function useDelayedFalse(value, delay = 230) {
  const [delayed, setDelayed] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (value) {
      setDelayed(true);
    } else {
      timerRef.current = setTimeout(() => setDelayed(false), delay);
    }
    return () => clearTimeout(timerRef.current);
  }, [value, delay]);

  return delayed;
}
